import { OnboardingVersionType } from '../states/OnboardingProvider';
import { NewPostContentType } from '../../../hooks/useOpenPostEditor';

export type OnboardingVersionKeysType = 'v1' | 'v2' | 'v3';
export const ProgresStepKeysV1 = [
  'v1_progress_categories',
  'v1_progress_posting_plan',
  'v1_progress_queue',
  'v1_progress_post_editor'
] as const;
export const ProgresStepKeysV2 = [
  'v2_progress_categories',
  'v2_progress_posting_plan',
  'v2_progress_queue',
  'v2_progress_post_editor'
] as const;
export const ProgresStepKeysV3 = [
  'v3_progress_categories',
  'v3_progress_posting_plan',
  'v3_progress_queue',
  'v3_progress_post_editor'
] as const;
export type ProgresStepType = {
  completed: boolean;
  key: (typeof ProgresStepKeysV1 | typeof ProgresStepKeysV2 | typeof ProgresStepKeysV3)[number];
  route: string;
  tooltips: Tooltip[];
  content: StepDataType;
};
export type Tooltip = {
  id: string;
  completed: boolean;
  registered: boolean;
  title: string;
  content: JSX.Element;
  opened: boolean;
};

export type StepDataType = {
  stepName: string;
} & SharedContentData;

export type CongratsDataType = {
  shorTitle: string;
  title: string;
} & SharedContentData;

export type SharedContentData = {
  content: string;
  learnMoreArticle: string | null;
  learnMoreText: string | null;
};

export type InitialOnboardingVersionType = Record<OnboardingVersionKeysType, ProgresStepType[]>;

export const InitialOnboardingSteps: InitialOnboardingVersionType = {
  v1: [
    {
      key: 'v1_progress_categories',
      route: '/publish/categories',
      completed: true,
      content: {
        stepName: 'Categories',
        content:
          'Categories keep your content organized, help automate your posting times, and give you detailed analytics.',
        learnMoreArticle: '729',
        learnMoreText: 'Learn more'
      },
      tooltips: [
        {
          id: 'v1_tourtip_categories_articles',
          registered: false,
          opened: false,
          completed: false,
          title: 'Organize Your Posts',
          content: (
            <>
              We’ve added some starter categories for you; think of them like content buckets for posts that share a
              common theme.
            </>
          )
        },
        {
          id: 'v1_tourtip_categories_demo',
          registered: false,
          opened: false,
          completed: false,
          title: 'Learn by Example',
          content: (
            <>
              <p className={'no-b-m'}>Our demo content will help you see how scheduling works in SmarterQueue.</p>
              <p className={'no-b-m'}>Follow the purple dots!</p>
            </>
          )
        }
      ]
    },
    {
      key: 'v1_progress_posting_plan',
      completed: false,
      route: '/publish/plan',
      content: {
        stepName: 'Posting Plan',
        content:
          'SmarterQueue automates your scheduling with Timeslots — set up your weekly posting plan, and we’ll handle the rest! Each Timeslot is linked to a Category and a social profile. Every week at the scheduled times, SmarterQueue will take the next Post from that Category’s Queue and publish it.',
        learnMoreArticle: '731',
        learnMoreText: 'Learn more'
      },
      tooltips: [
        {
          id: 'v1_tourtip_posting_plan_demo_profiles',
          completed: false,
          registered: false,
          opened: false,
          title: '',
          content: <></>
        }
      ]
    },
    {
      key: 'v1_progress_queue',
      completed: false,
      route: '/publish/queue',
      content: {
        stepName: 'Queue',
        content:
          'The Queue shows all your content — both auto-scheduled posts based on Timeslots, and those that are manually-scheduled with fixed dates. See what’s lined up next, customize your view, and use filters or search to quickly find specific posts.',
        learnMoreArticle: '732',
        learnMoreText: 'Learn more'
      },
      tooltips: [
        {
          id: 'v1_tourtip_queue_manage_post',
          completed: false,
          registered: false,
          opened: false,
          title: '',
          content: <></>
        }
      ]
    },
    {
      key: 'v1_progress_post_editor',
      completed: false,
      route: '', // no route is because post editor can be opened on any page
      content: {
        stepName: 'Post Editor',
        content:
          'The Post Editor is where you create and customize your posts. Click the purple dots to explore key features!',
        learnMoreArticle: '733',
        learnMoreText: 'Learn more'
      },
      tooltips: [
        {
          id: 'v1_tourtip_post_editor_select_category',
          completed: false,
          registered: false,
          opened: false,
          title: 'Category Profiles Selector',
          content: <>Content for Category Profile Selector</>
        },
        {
          id: 'v1_tourtip_post_editor_customize_per_profile',
          completed: false,
          registered: false,
          opened: false,
          title: 'Customize Posts',
          content: <>Temt text for customize</>
        },
        {
          id: 'v1_tourtip_post_editor_link',
          completed: false,
          registered: false,
          opened: false,
          title: 'Links in the text',
          content: <>Text for the links</>
        },
        {
          id: 'v1_tourtip_post_editor_post_preview',
          completed: false,
          registered: false,
          opened: false,
          title: 'Preview Post',
          content: <>See how your posts will look on each platform</>
        },
        {
          id: 'v1_tourtip_post_editor_post_timing',
          completed: false,
          registered: false,
          opened: false,
          title: 'Post Timing',
          content: <>Queue your post text</>
        }
      ]
    }
  ],
  v2: [],
  v3: []
};

export const getCongratsKey = (version: OnboardingVersionType): string => `v${version}_congrats_state`;

const pasuedContent: StepDataType = {
  stepName: 'Continue?',
  content: 'Ready to continue? Jump back into our demo to finish the quick tour!',
  learnMoreArticle: null,
  learnMoreText: null
};

const congratsConent: CongratsDataType = {
  shorTitle: '🎉 Congrats!',
  title: '🎉 Congratulations! Now it’s your turn!',
  content:
    'Connect your social profiles, schedule a quick test post, and see how SmarterQueue makes sharing your content effortless!',
  learnMoreArticle: '734',
  learnMoreText: 'Read our Fast Track Setup Guide'
};
export type AppEntities = 'categories' | 'posts' | 'timeslots' | 'profiles' | 'media';
export type DisableOnboardingActionsType = Record<AppEntities, boolean>;

export type OnboardingStaticContentType = {
  congrats: CongratsDataType;
  paused: StepDataType;
  disableDelete: DisableOnboardingActionsType;
  disableInvite: boolean;
};
export type OnboardingStaticContentVersionType = Record<OnboardingVersionKeysType, OnboardingStaticContentType>;
export const OnboardingStaticContent: OnboardingStaticContentVersionType = {
  v1: {
    congrats: congratsConent,
    paused: pasuedContent,
    disableDelete: {
      categories: true,
      posts: true,
      timeslots: true,
      profiles: true,
      media: false
    },
    disableInvite: true
  },
  v2: {
    congrats: congratsConent,
    paused: pasuedContent,
    disableDelete: {
      categories: true,
      posts: true,
      timeslots: true,
      profiles: true,
      media: false
    },
    disableInvite: true
  },
  v3: {
    congrats: congratsConent,
    paused: pasuedContent,
    disableDelete: {
      categories: true,
      posts: true,
      timeslots: true,
      profiles: true,
      media: false
    },
    disableInvite: true
  }
};

export const DEMO_CATEGORY_NAME = 'DEMO CATEGORY 🎓';

export const InitialPostEditorContent: NewPostContentType = {
  texts: ["Here's where you add your post caption and link https://smarterqueue.com"],
  link: 'https://smarterqueue.com'
};
